import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  XeroAccountConfig,
  XeroService
} from '../../../../shared/services/xero.service';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { XeroAccount } from '../../../../models/xero/xero-account';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { NewAccountDialogComponent } from './new-account-dialog/new-account-dialog.component';
import { ConfigService } from '../../../../shared/services/config.service';
import { AccountConfigType } from '../../../../models/account-config-type';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FlexModule } from '@angular/flex-layout';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { NgIf, NgFor } from '@angular/common';

@Component({
    selector: 'account-mappings',
    templateUrl: './account-mappings.component.html',
    providers: [XeroService],
    standalone: true,
    imports: [NgIf, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, NgFor, MatOption, FlexModule, MatProgressSpinner]
})
export class AccountMappingsComponent implements OnInit, OnDestroy {
  xeroAccounts: XeroAccount[];
  paymentableAccounts: XeroAccount[];
  directCostAccounts: XeroAccount[];
  salesRevenueAccounts: XeroAccount[];

  accountConfigKeys = AccountConfigType;
  private ngUnsubscribe = new Subject<void>();
  form = new UntypedFormGroup({});
  additionalCurrencies: string[] = [];
  defaultCurrency = 'GBP';

  @Input() set accountsForm(value: UntypedFormGroup) {
    this.form = value;
  }

  constructor(
    private xeroService: XeroService,
    private dialog: MatDialog,
    private configService: ConfigService
  ) {}

  ngOnInit() {
    this.configService.userConfig.subscribe((config) => {
      if ('currency' in config) {
        this.defaultCurrency = config['currency'];
      }
      if ('additional_currencies' in config) {
        this.additionalCurrencies = config['additional_currencies']
          .split(',')
          .map((value) => value.trim());
      }
      this.setupFormControls();
    });
    forkJoin(
      this.xeroService.getXeroAccounts(),
      this.xeroService.getConfiguredAccounts()
    )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([xeroAccounts, configuredAccounts]) => {
        this.xeroAccounts = xeroAccounts;
        this.sortAccountsByType();
        this.setInputs(configuredAccounts);
      });
  }

  createNewAccount(event: MouseEvent, option) {
    event.preventDefault();
    event.stopPropagation();
    this.dialog
      .open(NewAccountDialogComponent, {
        data: {
          type: this.getAccountType(option)
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((val) => {
        if (val) {
          this.xeroAccounts.push(val);
          this.sortAccountsByType();
          this.form.get(`xero_${option}`).patchValue(val.xero_id);
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getAccountType(option: string) {
    if (
      [
        AccountConfigType.CONSUMABLE_SALES_REVENUE_CODE,
        AccountConfigType.DELIVERY_REVENUE_CODE,
        AccountConfigType.HIRE_EQUIPMENT_SALES_REVENUE_CODE,
        AccountConfigType.HIRE_SALES_REVENUE_CODE,
        AccountConfigType.RETAIL_SALES_REVENUE_CODE,
        AccountConfigType.REPAIR_PARTS_REVENUE_CODE
      ].indexOf(option) > -1
    ) {
      return 'SALES';
    }
    if (
      [
        AccountConfigType.HIRE_EQUIPMENT_PURCHASES_CODE,
        AccountConfigType.CONSUMABLE_PURCHASE_CODE,
        AccountConfigType.RETAIL_PURCHASES_CODE
      ].indexOf(option) > -1
    ) {
      return 'DIRECTCOSTS';
    }

    return 'BANK';
  }

  private setInputs(configs: XeroAccountConfig) {
    Object.keys(configs).forEach((key) => {
      const control = this.form.get(key);
      if (!control) {
        throw new Error(
          `Incorrectly named form control for Xero accoung config: ${key}`
        );
      }
      control.patchValue(configs[key]);
    });
  }

  private setupFormControls() {
    // REVENUE
    this.form.addControl(
      `xero_${AccountConfigType.CONSUMABLE_SALES_REVENUE_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.DELIVERY_REVENUE_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.HIRE_EQUIPMENT_SALES_REVENUE_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.HIRE_SALES_REVENUE_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.RETAIL_SALES_REVENUE_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.REPAIR_PARTS_REVENUE_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.REPAIR_LABOUR_REVENUE_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );

    // COSTS
    this.form.addControl(
      `xero_${AccountConfigType.HIRE_EQUIPMENT_PURCHASES_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.RETAIL_PURCHASES_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.CONSUMABLE_PURCHASE_CODE}`,
      new UntypedFormControl(null, Validators.required)
    );

    // PAYMENTS
    this.form.addControl(
      `xero_${AccountConfigType.BANK_TRANSFER_PAYMENTS}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.CARD_PAYMENTS}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.CASH_PAYMENTS_PREFIX + this.defaultCurrency}`,
      new UntypedFormControl(null, Validators.required)
    );
    this.form.addControl(
      `xero_${AccountConfigType.CHEQUE_PAYMENTS_PREFIX + this.defaultCurrency}`,
      new UntypedFormControl(null, Validators.required)
    );
    // Additional currency payments
    this.additionalCurrencies.forEach((currency) => {
      this.form.addControl(
        `xero_${AccountConfigType.CASH_PAYMENTS_PREFIX}${currency}`,
        new UntypedFormControl(null, Validators.required)
      );
      this.form.addControl(
        `xero_${AccountConfigType.CHEQUE_PAYMENTS_PREFIX}${currency}`,
        new UntypedFormControl(null, Validators.required)
      );
    });
  }

  private sortAccountsByType() {
    this.paymentableAccounts = this.xeroAccounts.filter(
      (account) => account.canReceivePayments || account.type === 'BANK'
    );
    this.directCostAccounts = this.xeroAccounts.filter(
      (account) => account.type === 'DIRECTCOSTS'
    );
    this.salesRevenueAccounts = this.xeroAccounts.filter(
      (account) => account.type === 'SALES' || account.type === 'REVENUE'
    );
  }
}
